import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, CalendarDays, MapPin, CheckCircle2, Loader2, Lock, AlertTriangle, Clock } from "lucide-react";
import { publicFormsApi } from "@/lib/api";
import { FormFieldInput } from "@/components/FormFieldInput";
import { isVisible, isFileType } from "@/lib/formFields";
import { formatEventDate, formatTime } from "@/lib/helpers";

function Brand() {
  return (
    <div className="flex items-center gap-2">
      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary">
        <Sparkles className="h-4 w-4 text-gold" />
      </span>
      <span className="font-serif text-lg font-semibold tracking-tight">God<span className="text-gold">inga</span></span>
    </div>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-2xl px-5 py-8 sm:py-12">{children}</div>
    </div>
  );
}

export default function PublicForm() {
  const { eventSlug, formSlug } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [closed, setClosed] = useState(false);
  const [data, setData] = useState(null);
  const [answers, setAnswers] = useState({});
  const [files, setFiles] = useState({});
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await publicFormsApi.get(eventSlug, formSlug);
      if (d.event?.slug && d.event.slug !== eventSlug) navigate(`/event/${d.event.slug}/${formSlug}${window.location.search}`, { replace: true });
      setData(d);
      if (d.status === "closed") setClosed(true);
    } catch {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [eventSlug, formSlug, navigate]);

  useEffect(() => { load(); }, [load]);

  const setAnswer = (fid, val) => setAnswers((a) => ({ ...a, [fid]: val }));
  const setFieldFiles = (fid, arr) => setFiles((f) => ({ ...f, [fid]: arr }));

  const visibleFields = (data?.form?.fields || []).filter((f) => isVisible(f, answers));

  const submit = async () => {
    const form = data.form;
    for (const f of visibleFields) {
      if (!f.required) continue;
      if (isFileType(f.type)) {
        if (!(files[f.id] || []).length) { toast.error(`${f.label || "A file"} is required`); return; }
      } else {
        const v = answers[f.id];
        const empty = v === undefined || v === null || v === "" ||
          (Array.isArray(v) && v.length === 0) ||
          (typeof v === "object" && !Array.isArray(v) && !Object.values(v).some((x) => x));
        if (empty) { toast.error(`${f.label || "A field"} is required`); return; }
      }
    }
    if (form.consent_enabled && !consent) { toast.error("Please accept the consent to continue"); return; }
    setSubmitting(true);
    try {
      // only include files for currently visible fields
      const visIds = new Set(visibleFields.map((f) => f.id));
      const cleanFiles = Object.fromEntries(Object.entries(files).filter(([k]) => visIds.has(k)));
      const cleanAnswers = Object.fromEntries(Object.entries(answers).filter(([k]) => visIds.has(k)));
      const res = await publicFormsApi.submit(form.id, { answers: cleanAnswers, files: cleanFiles, consent });
      setDone(res);
      window.scrollTo(0, 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit the form");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <Shell><div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-gold" /></div></Shell>;
  }

  if (notFound) {
    return (
      <Shell>
        <div className="rounded-3xl border border-border bg-card p-10 text-center">
          <AlertTriangle className="mx-auto h-10 w-10 text-amber-500" />
          <h1 className="mt-4 font-serif text-2xl font-semibold">Form not found</h1>
          <p className="mt-2 text-sm text-muted-foreground">This link may be incorrect or the form is no longer available.</p>
        </div>
      </Shell>
    );
  }

  const { form, event } = data;

  if (done) {
    return (
      <Shell>
        <div className="mb-6"><Brand /></div>
        <div className="rounded-3xl border border-border bg-card p-10 text-center animate-fade-up" data-testid="form-success">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50">
            <CheckCircle2 className="h-9 w-9 text-emerald-600" />
          </div>
          <h1 className="mt-5 font-serif text-3xl font-semibold">Submission Successful</h1>
          <p className="mt-2 text-muted-foreground">Thank you{done.guest_name ? `, ${done.guest_name.split(" ")[0]}` : ""}! Your information has been submitted to the event team.</p>
          <div className="mt-6 inline-flex flex-col items-center rounded-2xl bg-secondary px-8 py-4">
            <span className="text-xs uppercase tracking-widest text-muted-foreground">Submission ID</span>
            <span className="mt-1 font-serif text-2xl font-bold text-gold" data-testid="form-success-code">{done.code}</span>
          </div>
          <p className="mt-6 text-xs text-muted-foreground">Please keep this ID for your reference.</p>
        </div>
      </Shell>
    );
  }

  if (closed) {
    return (
      <Shell>
        <div className="mb-6"><Brand /></div>
        <div className="rounded-3xl border border-border bg-card p-10 text-center" data-testid="form-closed">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-secondary">
            <Lock className="h-8 w-8 text-muted-foreground" />
          </div>
          <h1 className="mt-5 font-serif text-2xl font-semibold">This Form Is Closed</h1>
          <p className="mt-2 text-sm text-muted-foreground">This form is no longer accepting responses.</p>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="mb-6 flex items-center justify-between">
        <Brand />
      </div>

      {event.cover_image && (
        <div className="mb-5 h-40 overflow-hidden rounded-2xl">
          <img src={event.cover_image} alt={event.title} className="h-full w-full object-cover" />
        </div>
      )}

      <div className="rounded-3xl border border-border bg-card p-6 sm:p-8" data-testid="public-form">
        <p className="eyebrow text-gold">{event.event_type || "Event"}</p>
        <h1 className="mt-1 font-serif text-2xl font-semibold leading-tight sm:text-3xl">{event.title}</h1>
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
          {event.date && <span className="inline-flex items-center gap-1.5"><CalendarDays className="h-4 w-4 text-gold" />{formatEventDate(event.date)}{event.time ? `, ${formatTime(event.time)}` : ""}</span>}
          {event.venue_name && <span className="inline-flex items-center gap-1.5"><MapPin className="h-4 w-4 text-gold" />{event.venue_name}</span>}
        </div>

        <div className="my-6 border-t border-border" />

        <h2 className="font-serif text-xl font-semibold">{form.name}</h2>
        {form.description && <p className="mt-1 text-sm text-muted-foreground">{form.description}</p>}

        <div className="mt-6 space-y-6">
          {visibleFields.map((f) => (
            <div key={f.id} data-testid={`form-row-${f.id}`}>
              <label className="mb-1.5 block text-sm font-semibold">
                {f.label}
                {f.required && <span className="ml-1 text-destructive">*</span>}
              </label>
              {f.description && <p className="mb-2 text-xs text-muted-foreground">{f.description}</p>}
              <FormFieldInput
                field={f}
                value={answers[f.id]}
                files={files[f.id]}
                onChange={(v) => setAnswer(f.id, v)}
                onFiles={setFieldFiles}
              />
            </div>
          ))}

          {form.consent_enabled && (
            <label className="flex cursor-pointer items-start gap-3 rounded-xl bg-secondary/60 p-4 text-sm" data-testid="form-consent">
              <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 accent-[hsl(var(--gold))]" data-testid="form-consent-checkbox" />
              <span className="text-muted-foreground">{form.consent_text}</span>
            </label>
          )}

          <button
            onClick={submit}
            disabled={submitting}
            data-testid="form-submit-btn"
            className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary px-6 py-3.5 text-sm font-semibold text-primary-foreground transition-all hover:shadow-lg disabled:opacity-60"
          >
            {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Submitting…</> : "Submit Form"}
          </button>
          <p className="flex items-center justify-center gap-1.5 text-center text-xs text-muted-foreground">
            <Lock className="h-3 w-3" /> Your information is kept private and shared only with the event team.
          </p>
        </div>
      </div>
      <p className="mt-6 flex items-center justify-center gap-1.5 text-center text-xs text-muted-foreground">
        <Sparkles className="h-3 w-3 text-gold" /> Powered by Godinga Events
      </p>
    </Shell>
  );
}
